import { useCallback, useEffect, useState } from 'react';
import { apiClient } from '../../../auth-lib';
import { Button, Card, Input, Select, Skeleton, useToast } from '../../../design-system';
import '../../styles/Attendance.css';

// Who decides leave, and what kinds of leave exist.
//
// Two things on one screen because they answer the same question — "what
// happens when someone asks for time off" — and splitting them would mean a
// firm setting up leave has to find two places.
//
// ⚠️ Every field here must also be in updateAttendanceSettings' handling on the
// server. A field the getter returns but the setter ignores reads back fine and
// silently discards every save.

const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const COLORS = ['blue', 'amber', 'violet', 'teal', 'grey'];

function LeaveSettings() {
  const toast = useToast();
  const [leave, setLeave] = useState(null);
  const [types, setTypes] = useState([]);
  const [people, setPeople] = useState([]);
  const [canWrite, setCanWrite] = useState(false);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, t, u] = await Promise.all([
        apiClient.get('/api/attendance/settings'),
        apiClient.get('/api/attendance/leave-types'),
        apiClient.get('/api/attendance/team'),
      ]);
      setLeave(s.data.settings.leave || {});
      setCanWrite(s.data.canWrite);
      setTypes(t.data.types);
      setPeople(u.data.rows || []);
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not load leave settings');
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => { load(); }, [load]);

  const saveRouting = async () => {
    setSaving(true);
    try {
      await apiClient.put('/api/attendance/settings', { leave });
      toast.success('Leave settings saved');
      await load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not save');
    } finally {
      setSaving(false);
    }
  };

  const saveType = async (t) => {
    try {
      if (t._id) await apiClient.put(`/api/attendance/leave-types/${t._id}`, t);
      else await apiClient.post('/api/attendance/leave-types', t);
      toast.success('Leave type saved');
      setDraft(null);
      await load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not save that type');
    }
  };

  const deactivate = async (t) => {
    // eslint-disable-next-line no-alert
    if (!window.confirm(`Stop offering ${t.name}? Existing requests keep it.`)) return;
    try {
      await apiClient.delete(`/api/attendance/leave-types/${t._id}`);
      toast.success(`${t.name} deactivated`);
      await load();
    } catch (e) {
      toast.danger(e.response?.data?.error || 'Could not deactivate');
    }
  };

  if (loading || !leave) return <Card><Skeleton.Text lines={6} /></Card>;

  const overrides = leave.approverOverrides || [];
  const setL = (patch) => setLeave((l) => ({ ...l, ...patch }));

  return (
    <div className="att-page">
      <Card>
        <div className="att-head">
          <div>
            <h3 className="att-head-title">Who approves leave</h3>
            <p className="att-head-sub">
              Leave a request goes to one person by default; name an exception for anyone who
              reports elsewhere.
            </p>
          </div>
        </div>

        <div className="att-set-grid">
          <Select
            label="Default approver"
            value={leave.defaultApproverId || ''}
            disabled={!canWrite}
            onChange={(e) => setL({ defaultApproverId: e.target.value || null })}
            helperText="Leave unset and any manager can decide — which is also what happens if the named person leaves the firm."
          >
            <option value="">Any manager</option>
            {people.map((p) => <option key={p.userId} value={p.userId}>{p.name}</option>)}
          </Select>

          <Select
            label="Quota year starts in"
            value={leave.quotaYearStartMonth ?? 4}
            disabled={!canWrite}
            onChange={(e) => setL({ quotaYearStartMonth: Number(e.target.value) })}
            helperText="April is the Indian financial year — the year a practice thinks in."
          >
            {MONTHS.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
          </Select>

          <Input
            label="Notice expected (days)"
            type="number" min="0"
            value={leave.minNoticeDays ?? 0}
            disabled={!canWrite}
            onChange={(e) => setL({ minNoticeDays: Number(e.target.value) || 0 })}
            helperText="Advisory only — it warns, it does not refuse."
          />

          <label className="att-set-toggle att-set-wide">
            <input
              type="checkbox"
              checked={leave.allowPastDated !== false}
              disabled={!canWrite}
              onChange={(e) => setL({ allowPastDated: e.target.checked })}
            />
            <span>
              <b>Allow applying for a date that has passed</b>
              <small>
                On by default. Falling ill on Monday and applying on Wednesday is the common case,
                not an abuse — and the alternative is a manager correcting the register by hand.
              </small>
            </span>
          </label>
        </div>

        <h4 className="att-set-h">Exceptions</h4>
        {overrides.length === 0 && (
          <p className="att-empty" style={{ padding: '4px 0' }}>
            Everyone&apos;s leave goes to the default approver.
          </p>
        )}
        {overrides.map((o, i) => (
          <div className="att-hol-add" key={`${o.userId}-${i}`}>
            <Select
              value={String(o.userId)} disabled={!canWrite} aria-label="Employee"
              onChange={(e) => setL({
                approverOverrides: overrides.map((x, j) => (j === i ? { ...x, userId: e.target.value } : x)),
              })}
            >
              {people.map((p) => <option key={p.userId} value={p.userId}>{p.name}</option>)}
            </Select>
            <span className="att-muted">goes to</span>
            <Select
              value={String(o.approverId)} disabled={!canWrite} aria-label="Approver"
              onChange={(e) => setL({
                approverOverrides: overrides.map((x, j) => (j === i ? { ...x, approverId: e.target.value } : x)),
              })}
            >
              {people.map((p) => <option key={p.userId} value={p.userId}>{p.name}</option>)}
            </Select>
            {canWrite && (
              <Button size="sm" variant="ghost"
                onClick={() => setL({ approverOverrides: overrides.filter((_, j) => j !== i) })}>
                Remove
              </Button>
            )}
          </div>
        ))}
        {canWrite && people.length > 1 && (
          <Button size="sm" variant="secondary" onClick={() => setL({
            approverOverrides: [...overrides, {
              userId: people[0].userId,
              // Never the same person on both sides — the server refuses it and
              // defaulting to it would make the first click always invalid.
              approverId: people[1].userId,
            }],
          })}>
            + Add an exception
          </Button>
        )}

        {canWrite && (
          <div className="att-set-actions">
            <Button variant="primary" loading={saving} onClick={saveRouting}>Save leave settings</Button>
          </div>
        )}
      </Card>

      <Card>
        <div className="att-head">
          <div>
            <h3 className="att-head-title">Leave types</h3>
            <p className="att-head-sub">
              A quota is per person per year. Leave it empty for unlimited — which is what unpaid
              leave is.
            </p>
          </div>
          {canWrite && (
            <div className="att-head-actions">
              <Button size="sm" variant="secondary" onClick={() => setDraft({
                name: '', code: '', annualQuota: '', isPaid: true, requiresReason: true, colorKey: 'blue',
              })}>
                + New type
              </Button>
            </div>
          )}
        </div>

        <div className="att-scroll">
          <table className="att-table">
            <thead>
              <tr>
                <th>Name</th><th>Code</th><th className="att-num">Quota / year</th>
                <th>Paid</th><th>Reason</th>{canWrite && <th />}
              </tr>
            </thead>
            <tbody>
              {types.map((t) => (
                <tr key={t._id} style={t.isActive === false ? { opacity: 0.5 } : undefined}>
                  <td className="att-name">
                    {t.name}
                    {t.isActive === false && <span className="att-muted"> · inactive</span>}
                  </td>
                  <td className="att-muted">{t.code}</td>
                  <td className="att-num">{t.annualQuota == null ? 'Unlimited' : t.annualQuota}</td>
                  <td className="att-muted">{t.isPaid ? 'Paid' : 'Unpaid'}</td>
                  <td className="att-muted">{t.requiresReason === false ? 'Optional' : 'Required'}</td>
                  {canWrite && (
                    <td className="att-nowrap">
                      <button type="button" className="att-link" onClick={() => setDraft({ ...t })}>Edit</button>
                      {t.isActive !== false && (
                        <>
                          {' · '}
                          <button type="button" className="att-link" onClick={() => deactivate(t)}>Deactivate</button>
                        </>
                      )}
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {draft && (
          <div className="att-pay" style={{ marginTop: 14 }}>
            <span className="att-form-label">{draft._id ? `Edit ${draft.name}` : 'New leave type'}</span>
            <div className="att-set-grid">
              <Input label="Name" value={draft.name}
                onChange={(e) => setDraft({ ...draft, name: e.target.value })} />
              <Input label="Code" value={draft.code} disabled={!!draft._id}
                onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
                helperText={draft._id ? 'Fixed once created — reports group by it' : 'Short, e.g. CL'} />
              <Input
                label="Quota per year" type="number" min="0"
                value={draft.annualQuota ?? ''}
                onChange={(e) => setDraft({ ...draft, annualQuota: e.target.value === '' ? null : e.target.value })}
                helperText="Empty = unlimited"
              />
              <Select label="Colour" value={draft.colorKey || 'blue'}
                onChange={(e) => setDraft({ ...draft, colorKey: e.target.value })}>
                {COLORS.map((c) => <option key={c} value={c}>{c}</option>)}
              </Select>

              <label className="att-set-toggle">
                <input type="checkbox" checked={draft.isPaid !== false}
                  onChange={(e) => setDraft({ ...draft, isPaid: e.target.checked })} />
                <span><b>Paid leave</b></span>
              </label>
              <label className="att-set-toggle">
                <input type="checkbox" checked={draft.requiresReason !== false}
                  onChange={(e) => setDraft({ ...draft, requiresReason: e.target.checked })} />
                <span><b>A reason is required</b></span>
              </label>
            </div>
            <div className="att-set-actions">
              <Button variant="primary" size="sm" onClick={() => saveType(draft)}>Save type</Button>
              <Button variant="ghost" size="sm" onClick={() => setDraft(null)}>Cancel</Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}

export default LeaveSettings;
